import { useState } from 'react';
import { Button } from '../ui/button';
import { WorkingButton } from '../ui/working-button';
import {
  SAME_PERSON_CANCEL,
  SAME_PERSON_OPEN,
  SAME_PERSON_TOO_FEW,
  SAME_PERSON_WORKING,
  samePersonConfirmLabel,
  samePersonOutcome,
} from '../../lib/copy';

/**
 * **"These cards are one person"** (2026-10-12), for both lists that can bind a
 * card: the pick list and the `over_five` held list. The search can show one
 * person as two cards that nothing specific joined, and a single bind would
 * record the other card as somebody else. The office checks every card that is
 * this person; the checked cards bind together and only the rest are set aside.
 * Fewer than two checked is a single bind, which is the card's own button.
 */

export type Corrections = { original: string; corrected: string }[];

export interface SamePerson {
  /** `null` outside the mode; inside it, each checked card's near-match answers. */
  readonly checked: Record<string, Corrections> | null;
  open: () => void;
  close: () => void;
  /** The card's checkbox props in the mode, `undefined` outside it. */
  selectionFor: (
    ref: string,
  ) => { checked: boolean; onChange: (on: boolean, corrections: Corrections) => void } | undefined;
  /** The checked refs in the list's own order, and their corrections. */
  answer: (order: readonly string[]) => { refs: string[]; corrections: Corrections };
}

export function useSamePerson(): SamePerson {
  const [checked, setChecked] = useState<Record<string, Corrections> | null>(null);
  return {
    checked,
    open: () => setChecked({}),
    close: () => setChecked(null),
    selectionFor: (ref) =>
      checked
        ? {
            checked: ref in checked,
            onChange: (on, corrections) =>
              setChecked((current) => {
                const next = { ...current };
                if (on) next[ref] = corrections;
                else delete next[ref];
                return next;
              }),
          }
        : undefined,
    answer: (order) => {
      const refs = order.filter((ref) => checked !== null && ref in checked);
      return {
        refs,
        corrections: uniqueCorrections(refs.flatMap((ref) => checked?.[ref] ?? [])),
      };
    },
  };
}

export function SamePersonOpen({ disabled, onClick }: { disabled: boolean; onClick: () => void }) {
  return (
    <Button variant="act" disabled={disabled} onClick={onClick}>
      {SAME_PERSON_OPEN}
    </Button>
  );
}

export function SamePersonFoot({
  checked,
  total,
  working,
  onCancel,
  onConfirm,
}: {
  checked: Record<string, Corrections>;
  total: number;
  working: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const count = Object.keys(checked).length;
  return (
    <>
      <div className="src">{count < 2 ? SAME_PERSON_TOO_FEW : samePersonOutcome(count, total)}</div>
      <div className="layer-actions candidate-actions">
        <WorkingButton
          label={samePersonConfirmLabel(count)}
          workingLabel={SAME_PERSON_WORKING}
          working={working}
          disabled={count < 2}
          onClick={onConfirm}
        />
        <Button variant="act" disabled={working} onClick={onCancel}>
          {SAME_PERSON_CANCEL}
        </Button>
      </div>
    </>
  );
}

/** Two cards can carry the same near-match question; the same answer is sent once. */
export function uniqueCorrections(corrections: Corrections): Corrections {
  const seen = new Set<string>();
  return corrections.filter((c) => {
    const key = `${c.original}\u0000${c.corrected}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
